import { create } from 'zustand';

import { convertUrlInBucket } from '../../api/cdn';
import { getStudentEmailDomain } from '../../api/environment';
import { HomeworkSubmissionRepository } from '../../api/repository/homework-submission';
import { userError } from '../../stores/userError';
import { courseTitles } from './courseTitles';
import { emailPattern, toSubmission, studentEmailDomainUnset, type StudentSubmission } from './submissions';
import { type MaybeMsg } from '../../i18n/msg';

// a class over several school years stays well under this; older rows drop first
const SUBMISSION_LIMIT = 500;

// a forced reload of the same roster bumps this, so the older run stops writing
const generation: Record<string, number> = {};

export interface SubmissionState {
  key: string | null;
  loading: boolean;
  error: MaybeMsg | null;
  submissions: StudentSubmission[];
  courseTitles: Record<string, string>;
  /** Each roster's submissions for the session, an empty one included, so a tab switch reads nothing. */
  cache: Record<string, StudentSubmission[]>;
  load: (key: string, codes: string[], force?: boolean) => Promise<void>;
}

export const useSubmissionStore = create<SubmissionState>((set, get) => ({
  key: null,
  loading: false,
  error: null,
  submissions: [],
  courseTitles: {},
  cache: {},

  async load(key, codes, force = false) {
    const state = get();
    const cached = state.cache[key];
    if (!force && cached) {
      if (state.key !== key) set({ key, loading: false, error: null, submissions: cached });
      return;
    }
    if (!force && state.key === key && state.loading) return;
    const run = (generation[key] || 0) + 1;
    generation[key] = run;
    // a class left mid-load still caches its rows, but only the current one shows them
    const live = () => generation[key] === run;
    const shown = () => live() && get().key === key;
    const domain = getStudentEmailDomain();
    if (!domain) { set({ key, loading: false, error: studentEmailDomainUnset(), submissions: [] }); return; }
    const pattern = emailPattern(codes, domain);
    if (!pattern) { set({ key, loading: false, error: null, submissions: [] }); return; }
    set({ key, loading: true, error: null, submissions: cached || [] });
    try {
      const rows = await HomeworkSubmissionRepository.listByEmail(pattern, SUBMISSION_LIMIT);
      if (!live()) return;
      const submissions: StudentSubmission[] = [];
      rows.forEach((row) => {
        const submission = toSubmission(row, domain, convertUrlInBucket);
        if (submission) submissions.push(submission);
      });
      set({ cache: { ...get().cache, [key]: submissions } });
      if (shown()) set({ loading: false, submissions });
      // titles only label the rows, so the list shows before they land
      const titles = await courseTitles(submissions.map((s) => s.courseId).filter((id, i, all) => all.indexOf(id) === i), get().courseTitles);
      set({ courseTitles: { ...get().courseTitles, ...titles } });
    } catch (error) {
      if (shown()) set({ loading: false, error: userError(error) });
    }
  },
}));
